import type { StepNames, VSGAStep } from '~/lib/types';
import {
  backDriversideAnglePhotoValidationSelector,
  backPassengersideAnglePhotoValidationSelector,
  confirmationOfCorrectAutoDataValidationSelector,
  confirmationOfCorrectPhotoDataValidationSelector,
  contactInformationValidationSelector,
  driversidePhotoValidationSelector,
  frontDriversideAnglePhotoValidationSelector,
  frontPassengersideAnglePhotoValidationSelector,
  frontPhotoValidationSelector,
  kilometerPhotoValidationSelector,
  licensePlateValidationSelector,
  passengersidePhotoValidationSelector,
  preCheckValidationSelector,
  rearPhotoValidationSelector,
  roofLeftPhotoValidationSelector,
  roofRightPhotoValidationSelector,
  serialNumberPhotoValidationSelector,
  windshieldPhotoValidationSelector,
} from '~/state/validation';
import {
  AutoInfoSummary,
  Kilometers,
  LicensePlate,
  SerialNumber,
} from './Steps/Auto';
import { ContactInformation } from './Steps/ContactInformation';
import {
  DriverSidePhoto,
  FrontPhoto,
  PassengerSidePhoto,
  RearPhoto,
  WindshieldPhoto,
} from './Steps/Photos';
import {
  BackDriversideAngle,
  BackPassengersideAngle,
  FrontDriversideAngle,
  FrontPassengersideAngle,
} from './Steps/Photos/DiagonalPhotos';
import { LeftSideOfRoof } from './Steps/Photos/LeftSideOfRoof';
import { PhotosSummary } from './Steps/Photos/PhotosSummary';
import { RightSideOfRoof } from './Steps/Photos/RightSideOfRoof';
import { PhotosOverview } from './Steps/PhotosOverview';
import { PreCheck } from './Steps/PreCheck';
import { Welcome } from './Steps/Welcome';

export const vsgaStepConfig: VSGAStep = [
  {
    stepName: 'welcome',
    title: 'Online Vorschaden-Besichtigung',
    Component: Welcome,
    nextButtonText: 'vsga:general.nextButtonLetsGo',
  },
  {
    stepName: 'contact',
    title: 'vsga:contactData.title',
    Component: ContactInformation,
    validationSelector: contactInformationValidationSelector,
    nextButtonText: 'vsga:general.nextButton',
    nextButtonIcon: 'arrow-right-1',
  },
  {
    stepName: 'preCheck',
    title: 'vsga:preCheck.title',
    Component: PreCheck,
    validationSelector: preCheckValidationSelector,
    nextButtonText: 'vsga:general.nextButton',
    nextButtonIcon: 'arrow-right-1',
  },
  {
    stepName: 'photosOverview',
    title: 'vsga:photosOverview.title',
    Component: PhotosOverview,
    nextButtonText: 'vsga:licensePlate.title',
    nextButtonIcon: 'arrow-right-1',
  },
  {
    stepName: 'licensePlate',
    title: 'vsga:licensePlate.title',
    Component: LicensePlate,
    nextButtonText: 'vsga:kilometers.title',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: licensePlateValidationSelector,
  },
  {
    stepName: 'kilometers',
    title: 'vsga:kilometers.title',
    Component: Kilometers,
    nextButtonText: 'vsga:serialNumber.title',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: kilometerPhotoValidationSelector,
  },
  {
    stepName: 'serialnumber',
    title: 'vsga:serialNumber.title',
    Component: SerialNumber,
    nextButtonText: 'common:summary',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: serialNumberPhotoValidationSelector,
  },
  {
    stepName: 'autoSummary',
    title: 'vsga:autoInfoSummary.title',
    Component: AutoInfoSummary,
    validationSelector: confirmationOfCorrectAutoDataValidationSelector,
    nextButtonText: 'vsga:autoInfoSummary.nextButton',
    nextButtonIcon: 'arrow-right-1',
  },
  {
    stepName: 'front',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: FrontPhoto,
    nextButtonText: 'vsga:general.nextButtonPhotoWindshield',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: frontPhotoValidationSelector,
  },
  {
    stepName: 'windshield',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: WindshieldPhoto,
    nextButtonText: 'vsga:general.nextButtonFrontDriversideAngle',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: windshieldPhotoValidationSelector,
  },
  {
    stepName: 'frontDriversideAngle',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: FrontDriversideAngle,
    nextButtonText: 'vsga:general.nextButtonPhotoDriverSide',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: frontDriversideAnglePhotoValidationSelector,
  },
  {
    stepName: 'driverside',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: DriverSidePhoto,
    nextButtonText: 'vsga:general.nextButtonRoofLeft',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: driversidePhotoValidationSelector,
  },
  {
    stepName: 'roofLeft',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: LeftSideOfRoof,
    nextButtonText: 'vsga:general.nextButtonBackDriversideAngle',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: roofLeftPhotoValidationSelector,
  },
  {
    stepName: 'backDriversideAngle',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: BackDriversideAngle,
    nextButtonText: 'vsga:general.nextButtonPhotoRear',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: backDriversideAnglePhotoValidationSelector,
  },
  {
    stepName: 'rear',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: RearPhoto,
    nextButtonText: 'vsga:general.nextButtonBackPassengersideAngle',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: rearPhotoValidationSelector,
  },
  {
    stepName: 'backPassengersideAngle',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: BackPassengersideAngle,
    nextButtonText: 'vsga:general.nextButtonPhotoPassengerSide',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: backPassengersideAnglePhotoValidationSelector,
  },
  {
    stepName: 'passengerside',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: PassengerSidePhoto,
    nextButtonText: 'vsga:general.nextButtonRoofRight',
    validationSelector: passengersidePhotoValidationSelector,
  },
  {
    stepName: 'roofRight',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: RightSideOfRoof,
    nextButtonText: 'vsga:general.nextButtonFrontPassengersideAngle',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: roofRightPhotoValidationSelector,
  },
  {
    stepName: 'frontPassengersideAngle',
    title: 'vsga:damagePhoto.titlePhotosAndDamages',
    Component: FrontPassengersideAngle,
    nextButtonText: 'vsga:general.nextButtonFinished',
    nextButtonIcon: 'arrow-right-1',
    validationSelector: frontPassengersideAnglePhotoValidationSelector,
  },
  {
    stepName: 'photoSummary',
    title: 'vsga:damagePhoto.titlePhotosSummary',
    Component: PhotosSummary,
    validationSelector: confirmationOfCorrectPhotoDataValidationSelector,
    nextButtonText: 'vsga:general.sendButton',
  },
];

export function findVSGAStepIndex(stepName: StepNames) {
  const step = vsgaStepConfig.findIndex((step) => step.stepName === stepName);
  return step >= 0 ? step : 0;
}
